import { Inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpResponseBase
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { PagedPayload } from '../models/paged-payload';
import { AchFraudScan, Job } from '../models/job-models';
import {
  EmployeePayrollTotalsArgs,
  PayrollHistoryData,
  PayrollReportsModel,
  ReportModel,
  ReportPaydate,
  ReportRangeModel,
  SortOptions,
  TimeOffReportArgs,
  PayStubHistoryArgs,
  CreditDebitMemo,
  QuarterRangeModel
} from '../models/reports';
import { IdNamePair } from '../models/id-name-pair';
import { map, mergeMap } from 'rxjs/operators';
import { EmployeePayStubEmployee, EmployeePayStub } from '../models/employee-pay-stub';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any
  ) {}

  /// ACH Fraud Scan Methods ///

  public canScheduleAchScan(): Observable<boolean> {
    return this.http.get<boolean>(`${this.env.apiUrlRoot}/Reports/CanScheduleAchFraudScan`);
  }

  public GetAchFraudScanFile(id: number) {
    return this.http.get(`${this.env.apiUrlRoot}/Reports/AchFraudScans/${id}/File`, {
      responseType: 'blob'
    });
  }

  public getAchScans(
    pageNumber?: number,
    pageSize?: number
  ): Observable<PagedPayload<AchFraudScan>> {
    pageNumber = pageNumber || 1;
    pageSize = pageSize || 25;

    let params = new HttpParams();
    params = params.set('pageNum', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<PagedPayload<AchFraudScan>>(
      `${this.env.apiUrlRoot}/Reports/AchFraudScans`,
      { params: params }
    );
  }

  public scheduleAchScan(): Observable<AchFraudScan> {
    return this.http.post<AchFraudScan>(
      `${this.env.apiUrlRoot}/Reports/ScheduleAchFraudScan`,
      null
    );
  }

  public getCreditDebitMemos(clientCode: string, pageNumber?: number, pageSize?: number) {
    pageNumber = pageNumber || 1;
    pageSize = pageSize || 25;
    let params = new HttpParams();
    params = params.set('page_number', pageNumber.toString());
    params = params.set('per_page', pageSize.toString());
    return this.http.get<PagedPayload<CreditDebitMemo>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/CreditDebitMemos`,
      { params }
    );
  }

  public downloadMemo(clientCode: string, filename: string) {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/CreditDebitMemos/File`;
    let params = new HttpParams();
    params = params.set('filename', filename);
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');

    return this.http.get(uri, {
      headers,
      responseType: 'blob' as 'json',
      params
    });
  }

  public getReportRange(clientCode: string, type: string) {
    let params = new HttpParams();
    params = params.set('type', type);
    return this.http.get<ReportRangeModel>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/Range`,
      { params }
    );
  }

  public getQuarterlyReportRange(clientCode: string) {
    return this.http.get<QuarterRangeModel>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/Quarterly/Range`
    );
  }

  public getRecurringReports(
    earliestDate: Date,
    latestDate: Date,
    clientCode: string,
    type: string
  ) {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports`;
    let params = new HttpParams();
    params = params.set('earliestDate', earliestDate.toISOString());
    params = params.set('latestDate', latestDate.toISOString());
    params = params.set('type', type);
    return this.http.get<ReportModel[]>(uri, { params });
  }

  public getQuarterlyReports(clientCode: string, year: number, quarter: number) {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/${year}/${quarter}`;
    return this.http.get<ReportModel[]>(uri);
  }

  public getOnDemandReportsList(clientCode: string, location?: number | null) {
    const locationId = location ?? 0;
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Location/${locationId}/Reports/OnDemand`;
    return this.http.get<{ Id: number; Value: string }[]>(uri);
  }

  public downloadReports(clientCode: string, ids: number[], mimeType: string) {
    let params = new HttpParams();
    ids.forEach((id, index) => {
      params = params.set(`ids[${index}]`, id);
    });
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/Download/`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', mimeType);
    headers = headers.set('Accept', mimeType);

    return this.http.get(uri, {
      params,
      headers,
      responseType: 'blob' as 'json',
      observe: 'response'
    });
  }

  public downloadRecurringReport(
    clientCode: string,
    filenames: string[],
    mimeType: string,
    earliestDate: Date | string,
    latestDate: Date | string
  ) {
    let params = new HttpParams();
    filenames.forEach((id, index) => {
      params = params.set(`filenames[${index}]`, id);
    });
    const earliestString =
      typeof earliestDate == 'string' ? earliestDate : earliestDate.toISOString();
    const latestString = typeof latestDate == 'string' ? latestDate : latestDate.toISOString();

    params = params.set('earliestDate', earliestString);
    params = params.set('latestDate', latestString);
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/Recurring/Download/`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', mimeType);
    headers = headers.set('Accept', mimeType);

    return this.http.get(uri, {
      params,
      headers,
      responseType: 'blob' as 'json',
      observe: 'response'
    });
  }

  public downloadQuarterlyReport(
    clientCode: string,
    filenames: string[],
    mimeType: string,
    year: number,
    quarter: number
  ) {
    let params = new HttpParams();
    filenames.forEach((id, index) => {
      params = params.set(`filenames[${index}]`, id);
    });

    params = params.set('year', year);
    params = params.set('quarter', quarter);
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/Quarterly/Download/`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', mimeType);
    headers = headers.set('Accept', mimeType);

    return this.http.get(uri, {
      params,
      headers,
      responseType: 'blob' as 'json',
      observe: 'response'
    });
  }
  public dynamicReport(
    args: any,
    mimeType: string,
    clientCode: string,
    endpoint: string,
    location?: number
  ) {
    const locationId = location ?? 0;
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Location/${locationId}/Reports/${endpoint}`;
    let headers = new HttpHeaders();
    headers = headers.set('Accept', mimeType);

    return this.http.post(uri, args, { headers, responseType: 'blob' as 'json' });
  }
  public getPaydatesForClient(clientCode: string): Observable<ReportPaydate[]> {
    return this.http.get<ReportPaydate[]>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/Reports/ClientPayStubHistory/Paydates`
    );
  }

  public getEmployeePayrollHistory(
    clientCode: string,
    id: number,
    year: number
  ): Observable<EmployeePayStubEmployee> {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${id}/EmployeePaymentHistory/${year}`;
    return this.http.get<EmployeePayStubEmployee>(uri);
  }

  public getEmployeePayStub(
    clientCode: string,
    id: number,
    mimeType: string,
    args: PayStubHistoryArgs
  ) {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${id}/Reports/PayStubHistory/`;
    let headers = new HttpHeaders();
    headers = headers.set('Accept', mimeType);

    return this.http.post(uri, args, { headers, responseType: 'blob' as 'json' });
  }

  public getEmployeePayrollYears(clientCode: string, id: number): Observable<string[]> {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${id}/EmployeePaymentYears`;
    return this.http.get<string[]>(uri);
  }

  public getEmployeeReportYears(clientCode: string, id: number): Observable<string[]> {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${id}/EmployeeReportYears`;
    return this.http.get<string[]>(uri);
  }

  public downloadEmployeeReport(
    clientCode: string,
    ids: number[],
    subType: string,
    year: number,
    fileName: string,
    mimeType: string
  ) {
    let params = new HttpParams();
    ids.forEach((id, index) => {
      params = params.set(`ids[${index}]`, id);
    });
    params = params.set(`subType`, subType);
    params = params.set(`year`, year);
    params = params.set(`fileName`, fileName);
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/EmployeeReports/Download/`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', mimeType);
    headers = headers.set('Accept', mimeType);

    return this.http.get(uri, {
      params,
      headers,
      responseType: 'blob' as 'json',
      observe: 'response'
    });
  }

  //TODO: create employee report model for front end
  public getEmployeeReportHistory(clientCode: string, id: number, year: number): Observable<any> {
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${id}/EmployeeReportHistory/${year}`;
    return this.http.get<any>(uri);
  }

  public getPayrollHistory(clientCode: string, location?: number): Observable<ReportPaydate[]> {
    const locationId = location ?? 0;
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Location/${locationId}/PayrollHistory`;

    return this.http.get<PayrollHistoryData[]>(uri).pipe(
      map(data => {
        const final: ReportPaydate[] = [];
        data.forEach(record => {
          const yearInt = parseInt(record.Year);
          if (final.some(x => x.Year == yearInt)) {
            const group = final.find(x => x.Year == yearInt);
            group.Paydates.push({
              Paydate: record.PayDate,
              SequenceNumber: record.PaySeq
            });
          } else {
            final.push({
              Year: yearInt,
              Paydates: [
                {
                  Paydate: record.PayDate,
                  SequenceNumber: record.PaySeq
                }
              ]
            });
          }
        });
        return final;
      })
    );
  }
  public getSortOptions(clientCode: string, endpoint: string, location?: number) {
    const locationId = location ?? 0;
    const uri = `${this.env.apiUrlRoot}/Companies/${clientCode}/Location/${locationId}/Reports/${endpoint}/SortOptions`;
    return this.http.get<SortOptions[]>(uri);
  }
}
